import { pageTitle } from '@palaccount/config';
import type { UnitResponse } from '@palaccount/contracts';
import { Check, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { NativeSelect } from '@/components/ui/native-select';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { localizedName, localizedSymbol } from '@/lib/localized-name';
import { localizeServerMessage } from '@/lib/server-messages';

import { useUnitGroupMutations, useUnitGroups, useUnitMutations, useUnits } from './api';
import { InventoryTabs } from './inventory-tabs';

interface UnitFormState {
  name: string;
  /** S5 / UOM-01: the optional Arabic twins of `name` and `symbol`. */
  nameAr: string;
  symbol: string;
  symbolAr: string;
  groupId: string;
  precision: string;
  allowSales: boolean;
  allowPurchases: boolean;
}

const EMPTY: UnitFormState = {
  name: '',
  nameAr: '',
  symbol: '',
  symbolAr: '',
  groupId: '',
  precision: '0',
  allowSales: true,
  allowPurchases: true,
};

/**
 * MD-12 (owner ruling R6) — the unit vocabulary: list + a simple form. NO
 * factor column anywhere, and that is the point (§4): the conversion lives on
 * each item. Load starter units appears only while the list is empty.
 */
export function UnitsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<UnitResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [values, setValues] = useState<UnitFormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  /**
   * The form sits ABOVE the list, so clicking a unit far down the table
   * opened it out of sight (bugs: Units of Measure — "edit under the row, in
   * a pop-up, or move up to the form"). Each open bumps this, and the effect
   * below brings the form into view and puts the cursor in it.
   */
  const [openCount, setOpenCount] = useState(0);
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (openCount === 0) return;
    formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    document.getElementById('u-name')?.focus({ preventScroll: true });
  }, [openCount]);

  useEffect(() => {
    document.title = pageTitle(t('units.title'));
  }, [t]);

  const { data: units, isPending } = useUnits(showInactive);
  const { create, update, setActive, seedStarter } = useUnitMutations();
  const { data: unitGroups } = useUnitGroups(true);

  const openNew = () => {
    setEditing(null);
    setValues(EMPTY);
    setError(null);
    setFormOpen(true);
    setOpenCount((count) => count + 1);
  };
  const openEdit = (unit: UnitResponse) => {
    setEditing(unit);
    setValues({
      name: unit.name,
      nameAr: unit.nameAr ?? '',
      symbol: unit.symbol,
      symbolAr: unit.symbolAr ?? '',
      groupId: unit.groupId ?? '',
      precision: String(unit.precision),
      allowSales: unit.allowSales,
      allowPurchases: unit.allowPurchases,
    });
    setError(null);
    setFormOpen(true);
    setOpenCount((count) => count + 1);
  };

  const submit = async () => {
    setError(null);
    if (values.name.trim() === '') return setError(t('units.errName'));
    if (values.symbol.trim() === '' || values.symbol.trim().length > 10) {
      return setError(t('units.errSymbol'));
    }
    if (!/^[0-3]$/.test(values.precision.trim())) return setError(t('units.errPrecision'));
    const nameAr = values.nameAr.trim();
    const symbolAr = values.symbolAr.trim();
    const body = {
      name: values.name.trim(),
      symbol: values.symbol.trim(),
      precision: Number(values.precision),
      allowSales: values.allowSales,
      allowPurchases: values.allowPurchases,
    };
    try {
      if (editing) {
        // BR-MD-12-06: refiling any time; '' on edit means "unfile" (null) —
        // and an emptied Arabic box clears the twin the same way.
        await update.mutateAsync({
          unitId: editing.id,
          body: {
            ...body,
            nameAr: nameAr === '' ? null : nameAr,
            symbolAr: symbolAr === '' ? null : symbolAr,
            groupId: values.groupId === '' ? null : values.groupId,
          },
        });
      } else {
        await create.mutateAsync({
          ...body,
          ...(nameAr === '' ? {} : { nameAr }),
          ...(symbolAr === '' ? {} : { symbolAr }),
          ...(values.groupId === '' ? {} : { groupId: values.groupId }),
        });
      }
      setFormOpen(false);
    } catch (cause) {
      // The cause rides along: a structured refusal (D46 — nameTaken,
      // symbolTaken) carries key+params, so the sentence reaches an Arabic
      // reader in Arabic.
      setError(
        localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause), cause),
      );
    }
  };

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('units.title')}</h2>
        {/* MD-12 §6: the offer exists only while the table is EMPTY — a
            starter set has nothing to seed once a company has its own units,
            and that is not a permission question. */}
        {units !== undefined && units.length === 0 ? (
          <Gated permission="inventory.create" reasonKey="perm.cannot.createUnits">
          <Button
            variant="outline"
            onClick={() => {
              seedStarter.mutateAsync().catch((cause: unknown) => {
                setError(
                  localizeServerMessage(
                    t,
                    cause instanceof Error ? cause.message : String(cause),
                    cause,
                  ),
                );
              });
            }}
          >
            {t('units.loadStarter')}
          </Button>
          </Gated>
        ) : null}
        <Gated permission="inventory.create" reasonKey="perm.cannot.createUnits">
          <Button onClick={openNew}>
            <Plus aria-hidden /> {t('common.new')}
          </Button>
        </Gated>
      </div>

      {error && !formOpen ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {formOpen ? (
        <Card ref={formRef} className="scroll-mt-4">
          <CardHeader>
            <CardTitle>{t(editing ? 'units.editTitle' : 'units.newTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="grid gap-1.5">
                <Label htmlFor="u-name">{t('units.fName')}</Label>
                <Input
                  id="u-name"
                  maxLength={60}
                  value={values.name}
                  onChange={(event) => setValues({ ...values, name: event.target.value })}
                />
              </div>
              {/* S5 / UOM-01: the Arabic twins, each beside its primary. */}
              <div className="grid gap-1.5">
                <Label htmlFor="u-name-ar">{t('units.fNameAr')}</Label>
                <Input
                  id="u-name-ar"
                  dir="rtl"
                  lang="ar"
                  maxLength={60}
                  value={values.nameAr}
                  onChange={(event) => setValues({ ...values, nameAr: event.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="u-symbol">{t('units.fSymbol')}</Label>
                <Input
                  id="u-symbol"
                  dir="ltr"
                  maxLength={10}
                  className="font-mono"
                  value={values.symbol}
                  onChange={(event) => setValues({ ...values, symbol: event.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="u-symbol-ar">{t('units.fSymbolAr')}</Label>
                <Input
                  id="u-symbol-ar"
                  dir="rtl"
                  lang="ar"
                  maxLength={10}
                  value={values.symbolAr}
                  onChange={(event) => setValues({ ...values, symbolAr: event.target.value })}
                />
              </div>
              {/* Field 7 (OQ-05-18): an OPTIONAL pick over the folder list,
                  changeable at any time and deciding nothing (BR-MD-12-06). */}
              <div className="grid gap-1.5">
                <Label htmlFor="u-group">{t('units.fGroup')}</Label>
                <NativeSelect
                  id="u-group"
                  value={values.groupId}
                  onChange={(event) => setValues({ ...values, groupId: event.target.value })}
                >
                  <option value="">{t('units.groupNone')}</option>
                  {(unitGroups ?? [])
                    .filter((group) => group.isActive || group.id === values.groupId)
                    .map((group) => (
                      <option key={group.id} value={group.id}>
                        {localizedName(group, lang)}
                      </option>
                    ))}
                </NativeSelect>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="u-precision">{t('units.fPrecision')}</Label>
                <Input
                  id="u-precision"
                  dir="ltr"
                  inputMode="numeric"
                  className="font-mono"
                  value={values.precision}
                  onChange={(event) => setValues({ ...values, precision: event.target.value })}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-6">
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox
                  checked={values.allowSales}
                  onCheckedChange={(checked) => setValues({ ...values, allowSales: checked === true })}
                />
                {t('units.fAllowSales')}
              </Label>
              <Label className="flex items-center gap-2 font-normal">
                <Checkbox
                  checked={values.allowPurchases}
                  onCheckedChange={(checked) =>
                    setValues({ ...values, allowPurchases: checked === true })
                  }
                />
                {t('units.fAllowPurchases')}
              </Label>
            </div>
            <div className="flex gap-2">
              <Gated
                permission={editing ? 'inventory.edit' : 'inventory.create'}
                reasonKey={editing ? 'perm.cannot.editUnits' : 'perm.cannot.createUnits'}
              >
                <Button onClick={() => void submit()} disabled={!values.name.trim()}>
                  {t('common.save')}
                </Button>
              </Gated>
              <Button variant="outline" onClick={() => setFormOpen(false)}>
                {t('common.cancel')}
              </Button>
              {editing ? (
                <Gated permission="inventory.edit" reasonKey="perm.cannot.editUnits">
                <Button
                  variant="outline"
                  onClick={() => {
                    setActive
                      .mutateAsync({ unitId: editing.id, active: !editing.isActive })
                      .then(() => setFormOpen(false))
                      .catch((cause: unknown) =>
                        setError(
                          localizeServerMessage(
                            t,
                            cause instanceof Error ? cause.message : String(cause),
                            cause,
                          ),
                        ),
                      );
                  }}
                >
                  {t(editing.isActive ? 'items.switchOff' : 'items.switchOn')}
                </Button>
                </Gated>
              ) : null}
            </div>
            {editing ? <AuthorshipLine record={editing} /> : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="grid gap-4">
          <Label className="flex items-center gap-2 text-sm font-normal">
            <Checkbox
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
            {t('common.showInactive')}
          </Label>
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : (units ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('units.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('units.colCode')}</TableHead>
                  <TableHead>{t('units.colName')}</TableHead>
                  <TableHead>{t('units.colSymbol')}</TableHead>
                  <TableHead>{t('units.colGroup')}</TableHead>
                  <TableHead className="text-end">{t('units.colPrecision')}</TableHead>
                  <TableHead>{t('units.colSell')}</TableHead>
                  <TableHead>{t('units.colBuy')}</TableHead>
                  <TableHead className="text-end">{t('units.colItems')}</TableHead>
                  <TableHead>{t('units.colStatus')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(units ?? []).map((unit) => (
                  <TableRow
                    key={unit.id}
                    className="cursor-pointer"
                    onClick={() => openEdit(unit)}
                  >
                    <TableCell>
                      <Ltr value={unit.code} className="font-mono text-muted-foreground" />
                    </TableCell>
                    <TableCell className="font-medium">{localizedName(unit, lang)}</TableCell>
                    <TableCell>
                      <Ltr value={localizedSymbol(unit, lang)} className="font-mono" />
                    </TableCell>
                    {/* The folder's Arabic name comes off the groups list the
                        page already holds; `groupName` is the English join. */}
                    <TableCell className="text-muted-foreground">
                      {groupLabel(unit, unitGroups, lang)}
                    </TableCell>
                    <TableCell className="text-end font-mono">{unit.precision}</TableCell>
                    <TableCell>{unit.allowSales ? <Check className="size-4" aria-label={t('common.yes')} /> : '—'}</TableCell>
                    <TableCell>{unit.allowPurchases ? <Check className="size-4" aria-label={t('common.yes')} /> : '—'}</TableCell>
                    <TableCell className="text-end font-mono">{unit.itemsUsing}</TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {t(unit.isActive ? 'common.active' : 'common.inactive')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <UnitGroupsCard />
    </div>
  );
}

function groupLabel(
  unit: UnitResponse,
  groups: readonly { id: string; name: string; nameAr: string | null }[] | undefined,
  lang: string,
): string {
  if (unit.groupId === null) return '—';
  const group = groups?.find((row) => row.id === unit.groupId);
  return group === undefined ? (unit.groupName ?? '—') : localizedName(group, lang);
}

/**
 * The folder list (MD-12 §4's left panel, drawn as a card): UG-## codes,
 * rename in one place, switch off, and the one deliberately un-guarded
 * delete — a folder decides nothing (BR-MD-12-06), so deleting it simply
 * unfiles its units (MD-12 §15's own ruling). §4.20's confirm still asks.
 */
function UnitGroupsCard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [name, setName] = useState('');
  /** S5 / UOM-02: the optional Arabic twin, on create and on rename. */
  const [nameAr, setNameAr] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editNameAr, setEditNameAr] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: groups } = useUnitGroups(true);
  // The row whose Delete was pressed. A row-level dialog needs the RECORD and
  // not a boolean: one box is rendered for the whole table (below), so a flag
  // could not say which group it was about.
  const [deleting, setDeleting] = useState<NonNullable<typeof groups>[number] | null>(null);
  const { create, update, setActive, remove } = useUnitGroupMutations();

  const fail = (cause: unknown) =>
    setError(localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause)));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('unitGroups.title')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <p className="text-sm text-muted-foreground">{t('unitGroups.hint')}</p>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex flex-wrap items-end gap-2">
          <div className="grid gap-1.5">
            <Label htmlFor="ug-new">{t('unitGroups.fName')}</Label>
            <Input
              id="ug-new"
              maxLength={60}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ug-new-ar">{t('unitGroups.fNameAr')}</Label>
            <Input
              id="ug-new-ar"
              dir="rtl"
              lang="ar"
              maxLength={60}
              value={nameAr}
              onChange={(event) => setNameAr(event.target.value)}
            />
          </div>
          <Gated permission="inventory.create" reasonKey="perm.cannot.createUnitGroups">
            {/* NOT common.new: the units header already owns "+ New" on this
                screen, and two buttons with one accessible name is an
                accessibility defect before it is a test problem — the same
                class the permission-UI wave's aria-label lesson named, and
                the strict-mode violation that felled nine journeys. */}
            <Button
              variant="outline"
              disabled={!name.trim()}
              onClick={() => {
                setError(null);
                create
                  .mutateAsync({
                    name: name.trim(),
                    ...(nameAr.trim() === '' ? {} : { nameAr: nameAr.trim() }),
                  })
                  .then(() => {
                    setName('');
                    setNameAr('');
                  })
                  .catch(fail);
              }}
            >
              <Plus aria-hidden /> {t('unitGroups.new')}
            </Button>
          </Gated>
        </div>
        {(groups ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('unitGroups.empty')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('units.colCode')}</TableHead>
                <TableHead>{t('unitGroups.colName')}</TableHead>
                <TableHead className="text-end">{t('unitGroups.colUnits')}</TableHead>
                <TableHead>{t('units.colStatus')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(groups ?? []).map((group) => (
                <TableRow key={group.id}>
                  <TableCell>
                    <Ltr value={group.code} className="font-mono text-muted-foreground" />
                  </TableCell>
                  <TableCell className="font-medium">
                    {editingId === group.id ? (
                      <>
                        <Input
                          aria-label={t('unitGroups.renameNamed', { name: group.name })}
                          maxLength={60}
                          value={editName}
                          onChange={(event) => setEditName(event.target.value)}
                        />
                        <Input
                          aria-label={t('unitGroups.fNameAr')}
                          dir="rtl"
                          lang="ar"
                          maxLength={60}
                          className="mt-1.5"
                          value={editNameAr}
                          onChange={(event) => setEditNameAr(event.target.value)}
                        />
                        {/*
                          The unit GROUP is a master record of its own (one of
                          the nine), and this row is the only place it is ever
                          shown singly — there is no group detail screen — so
                          the four facts belong to the open rename.
                        */}
                        <AuthorshipLine record={group} />
                      </>
                    ) : (
                      localizedName(group, lang)
                    )}
                  </TableCell>
                  <TableCell className="text-end font-mono">{group.unitsIn}</TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground">
                      {t(group.isActive ? 'common.active' : 'common.inactive')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      {editingId === group.id ? (
                        <>
                          <Gated permission="inventory.edit" reasonKey="perm.cannot.editUnitGroups">
                            <Button
                              size="sm"
                              disabled={!editName.trim()}
                              aria-label={t('unitGroups.saveNamed', { name: group.name })}
                              onClick={() => {
                                setError(null);
                                update
                                  .mutateAsync({
                                    groupId: group.id,
                                    body: {
                                      name: editName.trim(),
                                      nameAr: editNameAr.trim() === '' ? null : editNameAr.trim(),
                                    },
                                  })
                                  .then(() => setEditingId(null))
                                  .catch(fail);
                              }}
                            >
                              {t('common.save')}
                            </Button>
                          </Gated>
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={t('unitGroups.cancelNamed', { name: group.name })}
                            onClick={() => setEditingId(null)}
                          >
                            {t('common.cancel')}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Gated permission="inventory.edit" reasonKey="perm.cannot.editUnitGroups">
                            <Button
                              size="sm"
                              variant="outline"
                              aria-label={t('unitGroups.editNamed', { name: group.name })}
                              onClick={() => {
                                setEditingId(group.id);
                                setEditName(group.name);
                                setEditNameAr(group.nameAr ?? '');
                                setError(null);
                              }}
                            >
                              {t('common.edit')}
                            </Button>
                          </Gated>
                          <Gated permission="inventory.edit" reasonKey="perm.cannot.editUnitGroups">
                            <Button
                              size="sm"
                              variant="outline"
                              aria-label={t('unitGroups.switchNamed', { name: group.name })}
                              onClick={() => {
                                setError(null);
                                setActive
                                  .mutateAsync({ groupId: group.id, active: !group.isActive })
                                  .catch(fail);
                              }}
                            >
                              {t(group.isActive ? 'items.switchOff' : 'items.switchOn')}
                            </Button>
                          </Gated>
                          <Gated
                            permission="inventory.delete"
                            reasonKey="perm.cannot.deleteUnitGroups"
                          >
                            <Button
                              size="sm"
                              variant="destructive"
                              aria-label={t('unitGroups.deleteNamed', { name: group.name })}
                              // §4.20 + MD-12 §15: allowed with units filed —
                              // they keep working, ungrouped. GEN-58 asks in
                              // the shared box (GEN-204/GEN-209), and the unit
                              // count goes with it: GEN-210 wants the count in
                              // the body where stock data is involved, which
                              // is exactly what `unitGroups.confirmDelete`
                              // already said and a flattened sentence would
                              // have thrown away.
                              onClick={() => setDeleting(group)}
                            >
                              {t('common.delete')}
                            </Button>
                          </Gated>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {/* ONE box for the whole table, keyed off the row that raised it.
            GEN-204: "a screen must not build its own." */}
        <DeleteConfirmBox
          open={deleting !== null}
          detail={
            deleting === null
              ? null
              : t('unitGroups.confirmDelete', {
                  name: deleting.name,
                  count: deleting.unitsIn,
                })
          }
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            const group = deleting;
            setDeleting(null);
            if (group === null) return;
            setError(null);
            remove.mutateAsync({ groupId: group.id }).catch(fail);
          }}
          busy={remove.isPending}
        />
      </CardContent>
    </Card>
  );
}
